import type { ReceiveConfirmContextDto, ReceiveConfirmLineDto } from '@/entities/receive';
import type { DataTableRow } from '@/shared/ui/data-table';

import { getConfirmLineKey } from '../model/types';

/**
 * One row of the lines table. A single consignment is a plain run of lines; a
 * waybill receipt spanning several puts a consignment header above each one's
 * lines — the shared table's full-width group row.
 */
export type ConfirmArrivalTableRow = DataTableRow &
  (
    | { kind: 'consignment'; consignmentNumber: string; organization?: string | null }
    | { kind: 'line'; line: ReceiveConfirmLineDto; index: number }
  );

/**
 * `index` is the line's position in `context.lines`, which is also its index in
 * the form's `lines` array. The backend orders lines by consignment, so a header
 * goes in wherever the shipment changes.
 */
export const buildConfirmArrivalTableRows = (context: ReceiveConfirmContextDto): ConfirmArrivalTableRow[] => {
  const shipments = context.shipments ?? [];
  const showConsignmentHeaders = shipments.length > 1;
  const organizationByShipment = new Map(
    shipments.map((shipment) => [shipment.shipmentId, shipment.organizationAbbreviation])
  );

  return context.lines.flatMap((line, index) => {
    const lineRow: ConfirmArrivalTableRow = { id: getConfirmLineKey(line), kind: 'line', line, index };
    if (!showConsignmentHeaders || line.shipmentId === context.lines[index - 1]?.shipmentId) return [lineRow];

    const header: ConfirmArrivalTableRow = {
      id: `${line.shipmentId}:consignment`,
      kind: 'consignment',
      consignmentNumber: line.consignmentNumber,
      organization: line.shipmentId ? organizationByShipment.get(line.shipmentId) : null
    };
    return [header, lineRow];
  });
};
